import { logger } from 'helpers';
import { LoadingAction, setDataAction } from 'store/actions';
import { type Dispatch, LoadingState } from 'store/state';
import { isString } from 'types';

export type HTTP = ReturnType<typeof buildHttp>;

export function buildHttp(token: string, dispatch: Dispatch) {
	const requestInit: RequestInit = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
	const cache: Map<string, Promise<unknown>> = new Map();
	return {
		delete(url: string, message?: string, req?: RequestInit) {
			return Request(url, dispatch, message, {
				...(req ?? requestInit),
				method: 'DELETE'
			});
		},
		fetcher(url: string) {
			return Request(url, dispatch, undefined, { ...requestInit, method: 'GET' });
		},
		get<T = unknown>(url: string, message?: string, req?: RequestInit): Promise<T> {
			let promise = cache.get(url) as Promise<T> | undefined;
			if (promise instanceof Promise) return promise;
			promise = Request(url, dispatch, message, { ...(req ?? requestInit), method: 'GET' }).then(data => {
				cache.delete(url);
				dispatch([setDataAction, { data: { [url]: data } }]);
				return data as T;
			});
			cache.set(url, promise);
			return promise;
		},
		post<T>(url: string, query: T, message?: string, req?: RequestInit) {
			const body = query instanceof FormData ? query : Query(query);
			return Request(url, dispatch, message, {
				...(req ?? requestInit),
				method: 'POST',
				body
			});
		},
		put<T>(url: string, data: T, req?: RequestInit, message?: string) {
			return Request(url, dispatch, message, {
				...(req ?? requestInit),
				method: 'PUT',
				body: JSON.stringify(data)
			});
		}
	};
}

async function Request(url: string, dispatch: Dispatch, message?: string, init: RequestInit = {}) {
	dispatch([LoadingAction, { loading: LoadingState(1, message) }]);
	const req = {
		...init,
		headers:
			init.body instanceof FormData
				? {
						...init.headers,
						'Content-Type': 'application/x-www-form-urlencoded'
					}
				: {
						...init.headers,
						Accept: 'application/json',
						'Content-Type': 'application/json'
					}
	};
	return fetch(url, req)
		.then((res: Response) => {
			dispatch([LoadingAction, { loading: LoadingState(-1) }]);
			if (res.status < 400) {
				return HandleResponse(res);
			}
			throw new Error(`Invalid response: ${res.status}`);
		})
		.catch(err => {
			dispatch([LoadingAction, { loading: LoadingState(-1) }]);
			logger(err);
			throw new Error(err.message);
		});
}

export function HandleResponse(res: Response) {
	return res.headers.get('content-type')?.includes('application/json') ? res.json() : res;
}

function Query<T>(query: T) {
	return isString(query) ? JSON.stringify({ query }) : JSON.stringify(query);
}
